/**
 * Menu Anchor Highlighting - Optional Enhancement
 *
 * This is an OPTIONAL feature for anchor link highlighting.
 * To disable: simply don't include this JS file.
 *
 * Features:
 * - Click anchor link → Highlights menu item + updates URL
 * - Scroll away from section → Removes highlight + removes hash
 * - Uses separate class (current-menu-item--anchor) to avoid conflicts
 */

(function () {
  "use strict";

  // Feature detection
  if (!("IntersectionObserver" in window)) {
    return;
  }

  const CONFIG = {
    // Reads the SAME --mobile-breakpoint CSS variable as
    // mobile-nav.js's getMobileBreakpoint() -- was previously
    // hardcoded to 768 here, which silently fell out of sync the
    // moment a site changed --mobile-breakpoint (a real, pre-existing
    // bug this fixes, not just a rename).
    mobileBreakpoint: null, // resolved lazily, see getMobileBreakpoint()
    anchorClass: "current-menu-item--anchor", // Separate class for anchor highlighting
    selectors: {
      // Hoisted here (was inline in getVisibleMenu()) so a site with
      // a custom menu_class (e.g. an overridden td_get_menu() using
      // something other than the default 'menu-primary') can update
      // ONE place instead of hunting through the function body.
      mobileMenu: ".mobile-menu",
      desktopMenu: ".menu-primary",
    },
  };

  let _cachedBreakpoint = null;
  function getMobileBreakpoint() {
    if (_cachedBreakpoint !== null) return _cachedBreakpoint;
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue("--mobile-breakpoint")
      .trim();
    const parsed = parseFloat(raw) * 16;
    _cachedBreakpoint = isNaN(parsed) ? 768 : parsed; // 768 only as a last-resort fallback if the CSS var is missing entirely
    return _cachedBreakpoint;
  }

  let currentHighlightedSection = null;
  let observer = null;

  /**
   * Check if we're in mobile viewport
   */
  function isMobile() {
    return window.innerWidth <= getMobileBreakpoint();
  }

  /**
   * Get the currently visible menu (desktop or mobile)
   */
  function getVisibleMenu() {
    return isMobile() ? CONFIG.selectors.mobileMenu : CONFIG.selectors.desktopMenu;
  }

  /**
   * Initialize
   */
  function init() {
    cleanupWordPressAnchorHighlighting();
    setupClickHandlers();

    const initialHash = window.location.hash.replace("#", "");
    if (initialHash) {
      const section = document.getElementById(initialHash);
      if (section) {
        highlightAnchor(initialHash);
        // Delay observer on page load
        setTimeout(() => {
          observeSection(section);
        }, 1000);
      }
    }

    window.addEventListener("hashchange", handleHashChange);
  }

  /**
   * Clean up WordPress's incorrect highlighting of anchor links on page load
   * WordPress adds current-menu-item to anchor links, which is wrong
   */
  function cleanupWordPressAnchorHighlighting() {
    const currentHash = window.location.hash;
    const currentPath = window.location.pathname.replace(/\/$/, "");

    // Always remove current-menu-item from anchor links (let our JS handle them)
    document.querySelectorAll('a[href*="#"]').forEach((link) => {
      const href = link.getAttribute("href");
      if (!href) return;

      const [path, hash] = href.split("#");
      const normalizedPath = path.replace(/\/$/, "");

      if (
        hash &&
        (normalizedPath === currentPath ||
          normalizedPath === "" ||
          normalizedPath === window.location.origin + currentPath)
      ) {
        const menuItem = link.closest(".menu-item");
        if (menuItem) {
          // Remove WordPress's incorrect class
          menuItem.classList.remove("current-menu-item");
        }
      }
    });
  }

  /**
   * Set up click handlers for anchor links
   */
  function setupClickHandlers() {
    const currentPath = window.location.pathname.replace(/\/$/, "");

    document.querySelectorAll('a[href*="#"]').forEach((link) => {
      link.addEventListener("click", function (e) {
        const href = this.getAttribute("href");
        if (!href) return;

        const [path, hash] = href.split("#");
        if (!hash) return;

        const normalizedPath = path.replace(/\/$/, "");

        const isCurrentPage =
          normalizedPath === currentPath ||
          normalizedPath === "" ||
          normalizedPath === window.location.origin + currentPath;

        if (isCurrentPage) {
          const section = document.getElementById(hash);
          if (section) {
            // Prevent default
            e.preventDefault();

            // Update URL
            history.pushState(null, null, "#" + hash);

            // Scroll (CSS handles offset via scroll-margin-top)
            section.scrollIntoView();

            // Highlight
            highlightAnchor(hash);

            // Wait for scroll to complete, then start observer
            waitForScrollComplete(section);
          }
        }
      });
    });
  }

  /**
   * Wait for scroll animation to complete before starting observer
   */
  function waitForScrollComplete(section) {
    function check() {
      const rect = section.getBoundingClientRect();
      const isInViewport = rect.top >= 0 && rect.top <= window.innerHeight;

      if (isInViewport) {
        observeSection(section);
      } else {
        requestAnimationFrame(check);
      }
    }

    setTimeout(() => {
      requestAnimationFrame(check);
    }, 100);
  }

  /**
   * Highlight anchor menu item
   */
  function highlightAnchor(sectionId) {
    const visibleMenuSelector = getVisibleMenu();
    const currentPath = window.location.pathname.replace(/\/$/, "");

    removeAllAnchorHighlights();
    removePageHighlighting();

    document
      .querySelectorAll(`${visibleMenuSelector} a[href*="#${sectionId}"]`)
      .forEach((link) => {
        const href = link.getAttribute("href");
        if (!href) return;

        const [path, hash] = href.split("#");
        const normalizedPath = path.replace(/\/$/, "");

        if (
          hash === sectionId &&
          (normalizedPath === currentPath ||
            normalizedPath === "" ||
            normalizedPath === window.location.origin + currentPath)
        ) {
          const menuItem = link.closest(".menu-item");
          if (menuItem) {
            // Add our anchor-specific class
            menuItem.classList.add(CONFIG.anchorClass);

            const parentItem = menuItem
              .closest(".sub-menu")
              ?.closest(".menu-item");
            if (
              parentItem &&
              !parentItem.classList.contains(CONFIG.anchorClass)
            ) {
              parentItem.classList.add(CONFIG.anchorClass);
            }
          }
        }
      });

    currentHighlightedSection = sectionId;
  }

  /**
   * Remove all anchor highlighting
   */
  function removeAllAnchorHighlights() {
    const visibleMenuSelector = getVisibleMenu();
    const currentPath = window.location.pathname.replace(/\/$/, "");

    document
      .querySelectorAll(`${visibleMenuSelector} a[href*="#"]`)
      .forEach((link) => {
        const href = link.getAttribute("href");
        if (!href) return;

        const [path, hash] = href.split("#");
        const normalizedPath = path.replace(/\/$/, "");

        if (
          hash &&
          (normalizedPath === currentPath ||
            normalizedPath === "" ||
            normalizedPath === window.location.origin + currentPath)
        ) {
          const menuItem = link.closest(".menu-item");
          if (menuItem) {
            menuItem.classList.remove(CONFIG.anchorClass);
          }
        }
      });
  }

  /**
   * Remove page item highlighting
   */
  function removePageHighlighting() {
    const visibleMenuSelector = getVisibleMenu();
    const currentPath = window.location.pathname.replace(/\/$/, "");

    document
      .querySelectorAll(`${visibleMenuSelector} .menu-item.current-menu-item`)
      .forEach((item) => {
        const link = item.querySelector(":scope > a");
        if (!link) return;

        const href = link.getAttribute("href");
        if (!href) return;

        const [path, hash] = href.split("#");
        const normalizedPath = path.replace(/\/$/, "");

        // If this is a page item (no hash), remove highlighting
        if (
          !hash &&
          (normalizedPath === currentPath ||
            normalizedPath === window.location.origin + currentPath)
        ) {
          item.classList.remove("current-menu-item");
        }
      });
  }

  /**
   * Restore page item highlighting
   */
  function restorePageHighlighting() {
    const visibleMenuSelector = getVisibleMenu();
    const currentPath = window.location.pathname.replace(/\/$/, "");

    document.querySelectorAll(`${visibleMenuSelector} a`).forEach((link) => {
      const href = link.getAttribute("href");
      if (!href) return;

      const [path, hash] = href.split("#");
      const normalizedPath = path.replace(/\/$/, "");

      if (
        !hash &&
        (normalizedPath === currentPath ||
          normalizedPath === window.location.origin + currentPath)
      ) {
        const menuItem = link.closest(".menu-item");
        if (menuItem) {
          menuItem.classList.add("current-menu-item");
        }
      }
    });
  }

  /**
   * Observe section for scroll-out
   */
  function observeSection(section) {
    if (observer) {
      observer.disconnect();
    }

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            const rect = section.getBoundingClientRect();
            const isAboveViewport = rect.bottom < 0;
            const isBelowViewport = rect.top > window.innerHeight;

            // Only clean up if section is completely out of viewport
            if (isAboveViewport || isBelowViewport) {
              removeAllAnchorHighlights();
              restorePageHighlighting();

              if (window.location.hash) {
                history.replaceState(null, null, window.location.pathname);
              }

              currentHighlightedSection = null;
              observer.disconnect();
              observer = null;
            }
          }
        });
      },
      {
        threshold: 0,
        rootMargin: "0px",
      },
    );

    observer.observe(section);
  }

  /**
   * Handle hash changes (browser back/forward)
   */
  function handleHashChange() {
    const hash = window.location.hash.replace("#", "");

    if (hash) {
      const section = document.getElementById(hash);
      if (section) {
        highlightAnchor(hash);
        setTimeout(() => {
          observeSection(section);
        }, 600);
      }
    } else {
      removeAllAnchorHighlights();
      restorePageHighlighting();
      currentHighlightedSection = null;
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
